import { type ArticleFromQueryArray } from "@lib/types";
import Link from "next/link";

import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { Badge } from "@/components/ui/badge";
import { icons } from "@/components/ui/icons";

import { capitalizeFirst, formatDate, getArticleUrl } from "./utils";

const ChevronRightIcon = icons.ChevronRight;

interface ArticleCardProps {
  article: ArticleFromQueryArray;
}

export function ArticleCard({ article }: ArticleCardProps) {
  return (
    <div className="font-header relative flex h-full flex-1 flex-col items-start gap-6 rounded-xl p-3 font-medium text-white">
      {/* Gradient Background Overlay */}
      <div className="absolute inset-0 z-0 rounded-xl [background-image:linear-gradient(180deg,_rgba(189,212,252,0.2),_rgba(178,178,178,0.2))]" />
      {/* Dark Background with Blend Mode */}
      <div className="bg-neutral-darkest absolute inset-0 z-0 rounded-xl opacity-5 mix-blend-soft-light" />

      {/* Image */}
      <div className="relative z-[1] aspect-video w-full shrink-0 overflow-hidden rounded-xl backdrop-blur-[12px]">
        {article.image ? (
          <ResponsiveImage
            src={article.image}
            fill
            className="object-cover"
            imageOptions={{ desktopWidth: 400 }}
          />
        ) : (
          <div className="h-full w-full bg-neutral-800" />
        )}
      </div>

      {/* Content */}
      <div className="relative z-[2] flex flex-1 flex-col items-start gap-6 self-stretch">
        {/* Badge and Read Time */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 text-xl leading-6">
          <Badge
            variant="outline"
            className="rounded-[100px] border-[1.5px] border-white bg-transparent px-[11px] py-[2.5px] text-xl font-normal text-white"
          >
            {capitalizeFirst(article._type)}
          </Badge>
          {/* <span className="font-header text-xl leading-6 font-medium text-white">5 min read</span> */}
        </div>

        {/* Title */}
        <h4 className="font-header relative z-[3] line-clamp-3 flex w-full items-start text-2xl leading-9 overflow-ellipsis text-white">
          <Link
            href={getArticleUrl(article)}
            className="text-2xl tracking-wider text-white no-underline"
          >
            {article.title}
          </Link>
        </h4>

        {/* Date */}
        {article.publishedAt ? (
          <time
            dateTime={article.publishedAt}
            className="text-neutral relative z-[4] text-xl leading-7 font-normal"
          >
            {formatDate(article.publishedAt)}
          </time>
        ) : null}

        {/* Read More Link */}
        <div className="relative z-[5] mt-auto flex flex-wrap items-center gap-2 self-stretch">
          <Link
            href={getArticleUrl(article)}
            className="font-header inline-flex items-center gap-2 text-xl leading-8 text-white no-underline transition-opacity hover:opacity-80"
          >
            <span>Read More</span>
            <ChevronRightIcon className="h-6 w-6" />
          </Link>
        </div>
      </div>
    </div>
  );
}
